const ACTIVE_STATES = new Set(['queued', 'running'])
const FAILED_RESULTS = new Set(['failed', 'failure', 'error'])
const KNOWN_ERROR_LABELS = {
  authentication_failed: 'Authentication failed',
  upstream_unavailable: 'Priority unavailable',
  mapping_failed: 'Mapping failed',
  partial_snapshot: 'Partial snapshot rejected',
  internal_error: 'Internal error',
}

const MODE_LABELS = {
  fixture: 'Demo fixtures',
  live: 'Live',
  disabled: 'Disabled',
}

const RECEIPT_CAPABILITY_LABELS = {
  stubbed: 'Stubbed',
  disabled: 'Disabled',
  test_only: 'Test only',
  deployed: 'Deployed',
  enabled: 'Deployed',
  unavailable: 'Unavailable',
}

const RECEIPT_OUTCOME_LABELS = {
  not_written: 'Matched locally · not written to Priority',
  pending: 'Pending Priority receipt',
  written: 'Written to Priority',
  failed: 'Priority receipt failed',
  reconciliation_required: 'Reconciliation required',
  ambiguous: 'Reconciliation required',
}

const asValue = (value) => String(value || '').trim().toLowerCase()

export function shortCorrelation(value) {
  const correlation = String(value || '').trim()
  return correlation ? correlation.slice(0, 8) : null
}

export function safeErrorLabel(code) {
  return KNOWN_ERROR_LABELS[asValue(code)] || (code ? 'Synchronization failed' : null)
}

export function formatAbsoluteTime(value) {
  if (!value) return 'Not available'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Not available'
  return new Intl.DateTimeFormat('en-IL', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date)
}

export function formatRelativeTime(value, referenceValue) {
  if (!value) return 'Never'
  const date = new Date(value)
  const reference = referenceValue ? new Date(referenceValue) : new Date()
  if (Number.isNaN(date.getTime()) || Number.isNaN(reference.getTime())) return 'Unknown'
  const seconds = Math.round((date.getTime() - reference.getTime()) / 1000)
  const absolute = Math.abs(seconds)
  if (absolute < 45) return seconds > 0 ? 'in a few seconds' : 'just now'
  const units = absolute < 3600
    ? ['minute', 60]
    : absolute < 86400
      ? ['hour', 3600]
      : ['day', 86400]
  const amount = Math.max(1, Math.round(absolute / units[1]))
  return seconds > 0 ? `in ${amount} ${units[0]}${amount === 1 ? '' : 's'}` : `${amount} ${units[0]}${amount === 1 ? '' : 's'} ago`
}

export function receiptCapabilityLabel(value) {
  return RECEIPT_CAPABILITY_LABELS[asValue(value)] || 'Not reported'
}

export function receiptOutcomeLabel(value, { matched = false } = {}) {
  const normalized = asValue(value)
  if (RECEIPT_OUTCOME_LABELS[normalized]) return RECEIPT_OUTCOME_LABELS[normalized]
  return matched ? RECEIPT_OUTCOME_LABELS.not_written : null
}

export function receiptOutcomeFrom(record, { matched = false } = {}) {
  const value = record?.receipt_outcome
    ?? record?.priority_receipt_outcome
    ?? record?.priority_receipt?.outcome
    ?? record?.priority_receipt?.status
  return receiptOutcomeLabel(value, { matched })
}

// Compatibility adapter for the fixture-only Collections API that currently
// ships with the local dashboard.  It deliberately reports only facts exposed
// by /health: there is no fabricated tenant, scheduler, version, timestamp, or
// live-sync capability.
export function legacyFixtureStatus(health) {
  if (!health || health.service !== 'collections-local') return null
  const loaded = health.loaded || {}
  const invoices = Number(loaded.invoices || 0)
  const openInvoices = Number(loaded.open_invoices || 0)
  const hasSnapshot = invoices > 0 || Number(loaded.customers || 0) > 0
  return {
    integration: 'priority',
    contract: 'legacy_fixture_health',
    mode: 'fixture',
    company: { id: 'demo_fixture', label: 'Local demo fixtures' },
    configuration: {
      auth: 'not_applicable',
      mapping: 'not_applicable',
      schedule_interval_hours: null,
    },
    versions: null,
    capabilities: {
      read: true,
      manual_sync: false,
      receipt_write: health.external_effects?.priority_write || 'stubbed',
    },
    sync: {
      state: hasSnapshot ? 'succeeded' : 'never',
      freshness: 'not_reported',
      snapshot_available: hasSnapshot,
      stale_after_seconds: null,
      next_scheduled_at: null,
      active: null,
      last_attempt: null,
      last_success: hasSnapshot ? {
        correlation_id: null,
        completed_at: null,
        duration_seconds: null,
        customers_synced: Number(loaded.customers || 0),
        invoices_synced: invoices,
        invoices_open: openInvoices,
        invoices_closed: Math.max(0, invoices - openInvoices),
      } : null,
    },
    server_time: null,
  }
}

export function legacyPriorityAvailability(health, { endpointMissing = false, loading = false } = {}) {
  const status = endpointMissing ? legacyFixtureStatus(health) : null
  return {
    status,
    unavailable: endpointMissing && !loading && !status,
  }
}

export function manualSyncControl(status, user) {
  const mode = asValue(status?.mode)
  const state = asValue(status?.sync?.state)
  const serverAllowed = status?.capabilities?.manual_sync === true
  const visible = serverAllowed && mode === 'live'
  const active = ACTIVE_STATES.has(state)
  let explanation = null
  if (visible && active) explanation = 'A synchronization is already in progress.'
  else if (!visible && user?.role === 'admin') explanation = 'Your dashboard role is admin, but the Collections API has not granted manual sync to this session.'
  else if (!visible) explanation = 'An authorized Collections administrator can request a read-only synchronization.'
  return { visible, enabled: visible && !active, serverAllowed, explanation }
}

export function derivePriorityPresentation(status, { unavailable = false } = {}) {
  if (unavailable || !status) {
    return {
      mode: 'unknown', modeLabel: 'Status unavailable', state: 'unavailable', tone: 'danger',
      headline: 'Priority integration status is unavailable',
      summary: 'The Collections status service could not be reached. Existing business data has not been cleared.',
      freshnessLabel: 'Unknown', latestFailed: false, active: false,
    }
  }

  const mode = asValue(status.mode) || 'unknown'
  const state = asValue(status.sync?.state) || 'idle'
  const freshness = asValue(status.sync?.freshness) || 'never'
  const lastAttemptResult = asValue(status.sync?.last_attempt?.result)
  const latestFailed = state === 'failed' || FAILED_RESULTS.has(lastAttemptResult)
  const active = ACTIVE_STATES.has(state)
  const hasSuccess = Boolean(status.sync?.last_success?.completed_at)
  const hasSnapshot = hasSuccess || status.sync?.snapshot_available === true
  const mapping = asValue(status.configuration?.mapping)
  const auth = asValue(status.configuration?.auth)
  const incomplete = state === 'incomplete'
    || (mode === 'live' && (['missing', 'invalid'].includes(mapping) || auth === 'missing'))

  let tone = 'success'
  let headline = 'Priority data is up to date'
  let summary = 'The last complete Priority snapshot is available.'

  if (mode === 'disabled' || state === 'disabled') {
    tone = 'warning'
    headline = 'Priority integration is disabled'
    summary = 'The Collections application is preserving its last known data. Scheduled and manual sync are unavailable.'
  } else if (incomplete) {
    tone = 'warning'
    headline = 'Live configuration is incomplete'
    summary = 'The accepted Priority mapping or connection readiness is incomplete. Ask the Priority integration owner to finish tenant discovery.'
  } else if (active) {
    tone = 'purple'
    headline = state === 'queued' ? 'Synchronization queued' : 'Synchronization running'
    summary = 'Counts below remain from the last complete snapshot until this read-only synchronization finishes.'
  } else if (latestFailed) {
    tone = 'danger'
    headline = safeErrorLabel(status.sync?.last_attempt?.error_code) || 'Latest synchronization failed'
    summary = hasSuccess
      ? 'The last complete snapshot is still displayed and has not been replaced by partial data.'
      : 'No completed Priority snapshot is available.'
  } else if (!hasSnapshot || state === 'never' || freshness === 'never') {
    tone = 'warning'
    headline = mode === 'fixture' ? 'Demo data has not been loaded' : 'No completed Priority snapshot yet'
    summary = mode === 'fixture'
      ? 'Priority-shaped synthetic data can be loaded locally. No Priority tenant is connected.'
      : 'Configuration readiness is shown below; data is not presented as fresh until a complete sync succeeds.'
  } else if (freshness === 'stale') {
    tone = 'warning'
    headline = 'Priority data is stale'
    summary = 'The page continues to show the last complete snapshot with its timestamp.'
  } else if (mode === 'fixture') {
    tone = 'neutral'
    headline = 'Demo fixtures loaded'
    summary = 'Priority-shaped synthetic data is loaded locally. No Priority tenant is connected.'
  }

  return {
    mode,
    modeLabel: MODE_LABELS[mode] || 'Unknown mode',
    state,
    tone,
    headline,
    summary,
    freshnessLabel: mode === 'disabled' && hasSnapshot
      ? 'Stale'
      : freshness === 'fresh'
        ? 'Fresh'
        : freshness === 'stale'
          ? 'Stale'
          : hasSnapshot && freshness === 'not_reported'
            ? 'Timestamp unavailable'
            : 'Never synchronized',
    latestFailed,
    active,
    incomplete,
  }
}

export function pollDelay(elapsedMilliseconds) {
  if (elapsedMilliseconds >= 120_000) return null
  return elapsedMilliseconds < 30_000 ? 2_000 : 5_000
}

export function pollObservation(status, correlationId, previouslyObserved = false) {
  const state = asValue(status?.sync?.state)
  const activeCorrelation = status?.sync?.active?.correlation_id
  const attemptCorrelation = status?.sync?.last_attempt?.correlation_id
  const matches = Boolean(correlationId && (activeCorrelation === correlationId || attemptCorrelation === correlationId))
  const observed = previouslyObserved || matches || ACTIVE_STATES.has(state)
  const complete = observed && !ACTIVE_STATES.has(state) && ['succeeded', 'failed'].includes(state)
  return { observed, complete, failed: complete && state === 'failed', state }
}

function provenanceCandidate(value) {
  if (!value || typeof value !== 'object') return null
  const mode = asValue(value.mode || value.source_mode)
  const integration = asValue(value.integration || value.system || value.source)
  if (!['fixture', 'live', 'disabled'].includes(mode) && !['priority', 'demo_fixture', 'priority_fixture'].includes(integration)) return null
  const lastAttempt = value.last_attempt || value.sync?.last_attempt
  return {
    mode: mode || (integration.includes('fixture') ? 'fixture' : 'live'),
    freshness: asValue(value.freshness || value.sync?.freshness),
    lastSuccessAt: value.last_success_at || value.last_complete_sync_at || value.last_success?.completed_at || value.sync?.last_success?.completed_at || null,
    latestFailed: value.latest_refresh_failed === true || FAILED_RESULTS.has(asValue(lastAttempt?.result)),
  }
}

export function priorityProvenance(...records) {
  for (const record of records) {
    const candidates = [
      record?.priority_provenance,
      record?.provenance?.priority,
      record?.provenance,
      record?.priority,
      record?.source,
      record?.source_metadata,
      record,
    ]
    for (const candidate of candidates) {
      const normalized = provenanceCandidate(candidate)
      if (normalized) return normalized
    }
  }
  return null
}

export function provenancePresentation(provenance, referenceValue) {
  if (!provenance) return null
  const sourceLabel = provenance.mode === 'fixture' ? 'Demo fixture' : 'Priority'
  const relative = provenance.lastSuccessAt ? formatRelativeTime(provenance.lastSuccessAt, referenceValue) : 'never synchronized'
  let warning = null
  if (provenance.latestFailed) warning = `Latest refresh failed; displaying data from ${relative}.`
  else if (provenance.freshness === 'stale') warning = `Priority data is stale; displaying the last complete data from ${relative}.`
  return {
    sourceLabel,
    lastSuccessAt: provenance.lastSuccessAt,
    relative,
    warning,
    text: provenance.lastSuccessAt ? `Source: ${sourceLabel} · last complete sync ${relative}` : `Source: ${sourceLabel} · no completed sync reported`,
  }
}
